import React, { useRef, useEffect, useState } from 'react';
import { StackLayout as Stack } from '@progress/kendo-react-layout';

/**
 * @component
 * @see {@link https://www.telerik.com/kendo-react-ui/components/layout/stacklayout/| KendoReact StackLayout Documentation}
 *
 * @description StackLayout - A reusable Kendo StackLayout component.
 *
 * @param {React.ReactNode} children - The elements to render inside the stack.
 * @param {'horizontal'|'vertical'} orientation - Stack direction. Default: 'horizontal'.
 * @param {string|number} gap - Gap between elements. Default: 0.
 * @param {Object} align - Alignment object, e.g. { horizontal: 'stretch', vertical: 'stretch' }.
 * @param {string} className - Additional CSS classes.
 * @param {React.CSSProperties} style - Inline styles.
 * @param {string} id - The id of the root StackLayout element.
 * @param {boolean} responsive - If true, enables responsive stacking based on container size.
 * @param {number} breakpoint - The container width (in px) at which to switch to vertical. Default: 600.
 * @returns {JSX.Element}
 *
 * @example
 * <Stack orientation="vertical" gap={16} align={{ horizontal: 'center' }}>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Stack>
 */
export const StackLayout = ({
  children,
  orientation = 'horizontal',
  gap = 0,
  align,
  className = '',
  style = {},
  id,
  responsive = false,
  breakpoint = 600,
  ...rest
}) => {
  const containerRef = useRef(null);
  const [currentOrientation, setCurrentOrientation] = useState(orientation);

  useEffect(() => {
    if (!responsive) return;
    const node = containerRef.current;
    if (!node) return;

    let animationFrameId = null;

  const handleResize = (entries) => {
    entries.forEach(entry => {
      const width = entry.contentRect.width;
      const newOrientation = width <= breakpoint ? 'vertical' : 'horizontal';
      animationFrameId = window.requestAnimationFrame(() => {
        setCurrentOrientation(prev => (prev !== newOrientation ? newOrientation : prev));
      });
    });
  };

    const resizeObserver = new window.ResizeObserver(handleResize);
    resizeObserver.observe(node);

    const initialOrientation = node.offsetWidth <= breakpoint ? 'vertical' : 'horizontal';
    setCurrentOrientation(prev => (prev !== initialOrientation ? initialOrientation : prev));

    return () => {
      resizeObserver.disconnect();
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [responsive, breakpoint]);

  const responsiveClass = responsive ? 'responsiveStack' : '';
  const mergedClassName = [className, responsiveClass].filter(Boolean).join(' ');
  const mergedStyle = responsive
    ? { ...style, '--stack-gap': gap, '--responsive-breakpoint': `${breakpoint}px` }
    : style;

  // Helper to render the Stack with the given orientation
  const renderStack = (stackOrientation) => (
    <Stack
      orientation={stackOrientation}
      gap={gap}
      align={align}
      className={mergedClassName}
      style={mergedStyle}
      id={id}
      {...rest}
    >
      {children}
    </Stack>
  );

  if (responsive) {
    return (
      <div ref={containerRef}>
        {renderStack(currentOrientation)}
      </div>
    );
  }

  return renderStack(orientation);
};